import { supabase } from '../../../core/lib/supabase';

/**
 * Datos de la wallet del usuario
 */
export interface UserWallet {
  user_id: string;
  subscription_balance: number;
  promo_balance: number;
  overage_limit: number;
  current_overage_spend: number;
  updated_at: string | null;
}

/**
 * Obtiene los datos de la wallet del usuario
 */
export async function getUserWallet(userId: string): Promise<UserWallet | null> {
  try {
    const { data, error } = await supabase
      .from('user_wallets')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      // Si no existe wallet, devolver null (no es un error crítico)
      if (error.code === 'PGRST116') {
        console.warn('⚠️ No wallet found for user:', userId);
        return null;
      }
      console.error('❌ Error fetching user wallet:', error);
      throw error;
    }

    return data as UserWallet;
  } catch (error) {
    console.error('💥 Error in getUserWallet:', error);
    throw error;
  }
}

/**
 * Verifica el estado financiero del usuario para operaciones de chat
 * Implementa la lógica del "semáforo":
 * 1. ¿subscription_balance > 0? → Luz verde
 * 2. Si no, ¿current_overage_spend < overage_limit? → Luz verde (overage disponible)
 * 3. Si ninguna → Luz roja (sin saldo)
 */
export interface FinancialStatus {
  canSendMessage: boolean;
  hasSubscriptionBalance: boolean;
  hasPromoBalance: boolean;
  hasOverageCapacity: boolean;
  subscriptionBalance: number;
  promoBalance: number;
  overageLimit: number;
  currentOverageSpend: number;
  availableOverage: number;
  reason?: 'subscription' | 'promo' | 'overage' | 'insufficient_funds' | 'no_wallet';
}

export function calculateFinancialStatus(wallet: UserWallet | null): FinancialStatus {
  // Si no hay wallet, no puede enviar
  if (!wallet) {
    return {
      canSendMessage: false,
      hasSubscriptionBalance: false,
      hasPromoBalance: false,
      hasOverageCapacity: false,
      subscriptionBalance: 0,
      promoBalance: 0,
      overageLimit: 0,
      currentOverageSpend: 0,
      availableOverage: 0,
      reason: 'no_wallet'
    };
  }

  const subscriptionBalance = Number(wallet.subscription_balance) || 0;
  const promoBalance = Number(wallet.promo_balance) || 0;
  const overageLimit = Number(wallet.overage_limit) || 0;
  const currentOverageSpend = Number(wallet.current_overage_spend) || 0;
  const availableOverage = overageLimit - currentOverageSpend;

  // Verificación 1: ¿Tiene saldo de suscripción?
  const hasSubscriptionBalance = subscriptionBalance > 0;
  
  // Verificación 2: ¿Tiene saldo promocional?
  const hasPromoBalance = promoBalance > 0;
  
  // Verificación 3: ¿Tiene capacidad de overage?
  const hasOverageCapacity = currentOverageSpend < overageLimit;

  // Determinar si puede enviar y la razón
  let canSendMessage = false;
  let reason: FinancialStatus['reason'] = 'insufficient_funds';

  if (hasSubscriptionBalance) {
    canSendMessage = true;
    reason = 'subscription';
  } else if (hasPromoBalance) {
    canSendMessage = true;
    reason = 'promo';
  } else if (hasOverageCapacity) {
    canSendMessage = true;
    reason = 'overage';
  }

  return {
    canSendMessage,
    hasSubscriptionBalance,
    hasPromoBalance,
    hasOverageCapacity,
    subscriptionBalance,
    promoBalance,
    overageLimit,
    currentOverageSpend,
    availableOverage,
    reason
  };
}

/**
 * Verifica rápidamente si el usuario puede enviar mensajes
 * Útil para validación antes de enviar
 */
export async function checkCanSendMessage(userId: string): Promise<FinancialStatus> {
  const wallet = await getUserWallet(userId);
  return calculateFinancialStatus(wallet);
}

/**
 * Actualiza el límite de overage del usuario
 */
export async function updateOverageLimit(userId: string, newLimit: number): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_wallets')
      .update({ 
        overage_limit: newLimit,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (error) {
      console.error('❌ Error updating overage limit:', error);
      throw error;
    }
  } catch (error) {
    console.error('💥 Error in updateOverageLimit:', error);
    throw error;
  }
}














